import React, { useState, useEffect, useCallback, useMemo } from 'react';
import CustomPaper from '../common/CustomPaper';
import Grid from '@mui/material/Grid';
import { FormControl } from '@mui/material';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import MentionEditor from '../TextEditor/MentionEditor';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import useApiExportStore, { getApiExportStore } from './useApiExportStore';
import CustomHeader from './Components/CustomHeader';
import { send_component_save_request, send_request } from '../../utils/Request';
import { injectMetadataFields } from '../../utils/buildComponentSavePayload';
import { getCurrentTime } from '../../utils/getCurrentTime';
import useProjectStore from '../ProjectCanvas/useProjectStore';
import AceEditor from 'react-ace';
import { addCompleter } from 'ace-builds/src-noconflict/ext-language_tools';
import { IndividualComponentLoader } from '../../utils/ComponentLoadingSkeleton';
import { validURL } from '../../utils/CanvasUtil';
import 'ace-builds/src-noconflict/mode-html';
import { v4 as uuidv4 } from 'uuid';

const ApiExportTemplate = (props) => {
  const [showHeaderFields, setShowHeaderFields] = useState(false);
  const [focus, setFocus] = useState(null);
  const [placeholdersState, setPlaceholdersState] = useState([]);
  const [loading, setLoading] = useState(false);
  const [urlError, setUrlError] = useState(false);
  const [urlErrorMessage, setUrlErrorMessage] = useState('');
  const [headerErrorMessage, setHeaderErrorMessage] = useState('');
  const [headerError, setHeaderError] = useState(false);
  const [keyPlaceholders, setKeyPlaceholders] = useState({});
  const [valuePlaceholders, setValuePlaceholders] = useState({});
  const [updateState, setUpdateState] = useState(false);
  const [description, setDescription] = useState(null);
  const [status, setStatus] = useState(null);

  const store = useMemo(() => getApiExportStore(props.component_id), [props.component_id]);

  const componentName = useProjectStore((s) => s.componentName);
  const componentDescription = useProjectStore((s) => s.componentDescription);

  const storeData = store((s) => s.data);
  const storePlaceholders = store((s) => s.placeholders);

  useEffect(() => {
    if (props.component_id === undefined) return;

    if (store.getState().loaded) {
      props.onComponentNameChanged(store.getState().name);
      props.onLastModifiedChanged(store.getState().lastModified);
      setLoading(false);
      props.setChildTemplateLoaded(true);
      return;
    }

    setLoading(true);

    send_request(
      `project-service/project/component/query/${props.component_id}/at-version/${props.version}`,
      '',
      {}
    )
      .then((response) => {
        if (response && response.data) {
          const { components } = response.data;
          if (components) {
            store.getState().setApiExportTemplate(components[0]);
            store.setState({ loaded: true });
            props.onComponentNameChanged(store.getState().name);
            props.onComponentDescriptionChanged(components[0].description);
            props.onLastModifiedChanged(store.getState().lastModified);
            setLoading(false);
          }
        }
        props.setChildTemplateLoaded(true);
        setLoading(false);
      })
      .then(() => {
        checkCondition();
      })
      .catch((err) => {
        setLoading(false);
        throw Error(err.message);
      });

    const placeholderParameters = [];
    if (
      props.availablePlaceholders &&
      props.availablePlaceholders.length === 1 &&
      props.availablePlaceholders[0] &&
      props.availablePlaceholders[0].placeholders &&
      props.availablePlaceholders[0].placeholders.length > 0
    ) {
      for (let i = 0; i < props.availablePlaceholders[0].placeholders.length; i++) {
        const completeKey = '${' + props.availablePlaceholders[0].placeholders[i].key + '}';
        const newCompleteItem = {
          name: completeKey,
          value: completeKey,
          caption: completeKey,
          meta: 'local',
          score: 1000,
        };
        placeholderParameters.push(newCompleteItem);
      }
    }
    addCompleter({
      getCompletions: function (editor, session, pos, prefix, callback) {
        callback(null, placeholderParameters);
      },
    });
  }, []);

  useEffect(() => {
    store.getState().setApiExportName(componentName);
  }, [componentName]);

  useEffect(() => {
    const unsub = useProjectStore.subscribe(
      (s) => s.selectedPlaceholder,
      (placeholder) => {
        const activeId = useProjectStore.getState().activeComponentId;
        if (activeId !== null && activeId !== props.component_id) return;
        if (!placeholder || !placeholder['selected'] || typeof placeholder['selected'] !== 'object') return;
        const { style, key } = placeholder['selected'];
        if (!style) return;
        insertPlaceholder(style.default, key, style, focus);
      }
    );
    return unsub;
  }, [focus, keyPlaceholders, valuePlaceholders]);

  const checkCondition = useCallback(() => {
    let hasPlaceholder = false;
    const { availablePlaceholders } = props;
    if (availablePlaceholders) {
      availablePlaceholders.forEach((availablePlaceholder) => {
        availablePlaceholder.placeholders.forEach((placeholder) => {
          if (store.getState().data.endpoint_url.includes(placeholder.key)) {
            hasPlaceholder = true;
          }
        });
      });
    }

    setHeaderError(false);
    setHeaderErrorMessage('');
    setUrlError(false);
    setUrlErrorMessage('');

    if (hasPlaceholder) {
      setUrlError(false);
      setUrlErrorMessage('');
      props.onChangeState && props.onChangeState(1, 1);
    } else {
      if (validURL(store.getState().data.endpoint_url)) {
        setUrlError(false);
        setUrlErrorMessage('');
        props.onChangeState && props.onChangeState(1, 1);
      } else {
        setUrlError(true);
        setUrlErrorMessage('Invalid or empty endpoint URL');
      }
    }

    store.getState().data.headers.forEach((h) => {
      if (h.header_name == '' || h.header_value == '') {
        setHeaderError(true);
        setHeaderErrorMessage('Please fill in the header fields. Header fields must not be empty.');
      }
    });
  }, [props.availablePlaceholders]);

  const insertPlaceholder = useCallback(
    (label, text, style, currentFocus) => {
      if (typeof currentFocus === 'object' && currentFocus !== null) {
        const { headerType, index } = currentFocus;
        const placeholder = '${' + text + '}';
        if (headerType === 'key') {
          const storeState = store.getState();
          const headers = [...storeState.data.headers];
          headers[index] = { ...headers[index], header_name: headers[index].header_name + placeholder };
          store.setState({ data: { ...storeState.data, headers } });

          const newKeysPlaceholders = { ...keyPlaceholders };
          if (!(index in newKeysPlaceholders)) {
            newKeysPlaceholders[index] = [{ text }];
          } else {
            newKeysPlaceholders[index] = [...newKeysPlaceholders[index], { text }];
          }
          setKeyPlaceholders(newKeysPlaceholders);
        } else {
          const storeState = store.getState();
          const headers = [...storeState.data.headers];
          headers[index] = { ...headers[index], header_value: headers[index].header_value + placeholder };
          store.setState({ data: { ...storeState.data, headers } });

          const newValuePlaceholders = { ...valuePlaceholders };
          if (!(index in newValuePlaceholders)) {
            newValuePlaceholders[index] = [{ text }];
          } else {
            newValuePlaceholders[index] = [...newValuePlaceholders[index], { text }];
          }
          setValuePlaceholders(newValuePlaceholders);
        }
        return;
      }

      if (currentFocus === 'editor') {
        setPlaceholdersState((prev) => [...prev, { label, text, style, focus: currentFocus }]);
      } else if (currentFocus === 'body') {
        const formattedText = '${' + text + '}';
        const template = store.getState().getTemplate();
        const newText = template.data.body + formattedText;
        store.getState().setApiExportBody(newText);
      } else if (currentFocus === 'endpoint') {
        const phs = [...store.getState().placeholders, { label, text, style }];
        store.getState().setPlaceholders(phs);
      }
    },
    [keyPlaceholders, valuePlaceholders]
  );

  const insertHeaders = () => {
    const newHeader = { header_id: uuidv4(), header_name: '', header_value: '' };
    const storeState = store.getState();
    const headers = [...storeState.data.headers, newHeader];
    store.setState({ data: { ...storeState.data, headers } });
    checkCondition();
  };

  const handleChange = (event) => {
    const name = event.target.name;
    const value = event.target.value;
    if (name === 'method') {
      store.getState().setRequestType(value);
    }
    if (name === 'dataType') {
      store.getState().setDataType(value);
    }
  };

  const setupModeChange = (mode) => {
    store.getState().setSetupMode(mode);
  };

  const forceUpdateState = () => {
    setUpdateState((prev) => !prev);
  };

  const keyArray = [...storePlaceholders];

  if (!loading) {
    return (
      <Grid container xs={12} className="componentGrid">
        <CustomPaper className="componentPaper">
          <Grid
            container
            xs={12}
            style={{ width: '100%', padding: '24px 24px 12px 24px', background: 'white' }}
            className="api-editor-container"
          >
            <Grid item style={{ width: '100%' }}>
              <FormControl style={{ width: '50%' }}>
                <Grid
                  item
                  xs={12}
                  style={{
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontFamily: 'Open Sans',
                    fontWeight: 'normal',
                    lineHeight: '19px',
                    color: '#868686',
                  }}
                >
                  Setup Mode
                </Grid>
                <Select
                  name="method"
                  labelId="demo-simple-select-outlined-label"
                  className="dropdown-mat"
                  value={storeData.setupMode}
                  defaultValue={'MANUAL'}
                  disableUnderline
                  variant="standard"
                  align="left"
                  onChange={(value) => setupModeChange(value.target.value)}
                >
                  <MenuItem value={'MANUAL'}>Manually set each field</MenuItem>
                  <MenuItem value={'CURL'}>Write cURL</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CustomPaper>

        <div hidden={storeData.setupMode !== 'MANUAL'} style={{ width: '100%' }}>
          <CustomPaper className="componentPaper">
            <Grid
              container
              style={{ width: '100%', padding: '24px 24px 24px 24px', background: 'white' }}
              className="api-editor-container"
            >
              <Grid item style={{ width: '100%' }}>
                <FormControl style={{ width: '50%' }}>
                  <Grid
                    item
                    xs={12}
                    style={{
                      marginBottom: '8px',
                      fontSize: '14px',
                      fontFamily: 'Open Sans',
                      fontWeight: 'normal',
                      lineHeight: '19px',
                      color: '#868686',
                    }}
                  >
                    Method
                  </Grid>
                  <Select
                    name="method"
                    labelId="demo-simple-select-outlined-label"
                    className="dropdown-mat"
                    value={storeData.request_type}
                    defaultValue={'POST'}
                    disableUnderline
                    variant="standard"
                    align="left"
                    onChange={handleChange}
                  >
                    <MenuItem value={'POST'}>POST</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} style={{ width: '100%' }}>
                <FormControl fullWidth style={{ marginTop: '24px', width: '50%' }}>
                  <Grid
                    item
                    xs={12}
                    style={{
                      marginBottom: '8px',
                      fontSize: '14px',
                      fontFamily: 'Open Sans',
                      fontWeight: 'normal',
                      lineHeight: '19px',
                      color: '#868686',
                    }}
                  >
                    Content Type
                  </Grid>
                  <Select
                    name="dataType"
                    labelId="demo-simple-select-outlined-label"
                    className="dropdown-mat"
                    defaultValue={'JSON'}
                    value={storeData.data_type}
                    disableUnderline
                    variant="standard"
                    align="left"
                    onChange={handleChange}
                  >
                    <MenuItem value={'JSON'}>JSON</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <FormControl fullWidth style={{ marginTop: '24px' }}>
                <Grid
                  item
                  xs={12}
                  style={{
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontFamily: 'Open Sans',
                    fontWeight: 'normal',
                    lineHeight: '19px',
                    color: '#868686',
                  }}
                >
                  Endpoint URL
                </Grid>
                <Grid item xs={12} className="width-100">
                  <MentionEditor
                    availablePlaceholders={props.availablePlaceholders}
                    placeholders={keyArray}
                    text={storeData.endpoint_url}
                    placeholder="Insert API endpoint URL here"
                    onChange={(value) => {
                      store.getState().setEndPointUrl(value);
                      checkCondition();
                    }}
                    onFocus={() => setFocus('endpoint')}
                    error={urlError ? true : false}
                    helperText={urlError ? urlErrorMessage : null}
                  />
                </Grid>
              </FormControl>
              <FormControl fullWidth style={{ marginTop: '24px' }}>
                <Grid item xs={12} className="bodyInput">
                  Body
                </Grid>
                <Grid item xs={12} style={{ width: '100%' }}>
                  <div id="aceditor" style={{ width: '100%', height: '700px' }}>
                    <AceEditor
                      mode="javascript"
                      theme="github"
                      width="100%"
                      height="100%"
                      showPrintMargin={false}
                      showGutter={true}
                      highlightActiveLine={true}
                      wrapEnabled={true}
                      value={storeData.body}
                      onChange={(html) => {
                        if (html !== storeData.body) {
                          store.getState().setApiExportBody(html);
                        }
                      }}
                      fontSize={16}
                      setOptions={{
                        enableBasicAutocompletion: true,
                        enableLiveAutocompletion: true,
                        enableSnippets: false,
                        showLineNumbers: true,
                        tabSize: 2,
                      }}
                      editorFocus={() => setFocus('editor')}
                    />
                  </div>
                </Grid>
              </FormControl>
              {storeData.headers.length ? (
                <FormControl fullWidth style={{ marginTop: '24px', color: 'var(--color-text-secondary)' }}>
                  <Grid
                    item
                    xs={12}
                    style={{
                      marginBottom: '8px',
                      fontSize: '14px',
                      fontFamily: 'Open Sans',
                      fontWeight: 'normal',
                      lineHeight: '19px',
                    }}
                  >
                    Custom Header
                  </Grid>
                  <CustomHeader
                    availablePlaceholders={props.availablePlaceholders}
                    onFocus={(f) => setFocus(f)}
                    validateRequest={() => checkCondition()}
                    keyPlaceholders={keyPlaceholders}
                    valuePlaceholders={valuePlaceholders}
                    headerError={headerError}
                  />
                </FormControl>
              ) : (
                <Grid></Grid>
              )}
              <Grid item xs={12}></Grid>
              <Grid item xs={12}>
                <Button variant={'text'} className={'custom-icon-btn'} onClick={insertHeaders}>
                  <AddIcon /> Add Header
                </Button>
              </Grid>
            </Grid>
          </CustomPaper>
        </div>

        <div hidden={storeData.setupMode !== 'CURL'} style={{ width: '100%' }}>
          <CustomPaper className="componentPaper">
            <Grid
              container
              style={{ padding: '12px 24px 24px 24px', background: 'white' }}
              className="api-editor-container"
            >
              <Grid item xs={12} style={{ width: '100%' }}>
                <FormControl fullWidth style={{ marginTop: '24px' }}>
                  <Grid
                    item
                    xs={12}
                    style={{
                      marginBottom: '8px',
                      fontSize: '14px',
                      fontFamily: 'Open Sans',
                      fontWeight: 'normal',
                      lineHeight: '19px',
                      color: '#868686',
                    }}
                  >
                    cURL
                  </Grid>
                  <Grid item xs={12} style={{ width: '100%' }}>
                    <div id="aceditor" style={{ width: '100%', height: '200px' }}>
                      <AceEditor
                        mode="javascript"
                        theme="github"
                        width="100%"
                        height="100%"
                        showPrintMargin={false}
                        showGutter={true}
                        highlightActiveLine={true}
                        value={storeData.curl}
                        wrapEnabled={true}
                        onChange={(value) => {
                          if (value) {
                            store.getState().setCurl(value);
                            forceUpdateState();
                          }
                        }}
                        fontSize={16}
                        setOptions={{
                          enableBasicAutocompletion: true,
                          enableLiveAutocompletion: true,
                          enableSnippets: false,
                          showLineNumbers: true,
                          tabSize: 2,
                        }}
                        editorFocus={() => setFocus('curl')}
                      />
                    </div>
                  </Grid>
                </FormControl>
              </Grid>
            </Grid>
          </CustomPaper>
        </div>
      </Grid>
    );
  } else {
    return <IndividualComponentLoader />;
  }
};

export default ApiExportTemplate;
